import SelectShared from '../../../../components/Select/Select';
import { useEffect, useState, useMemo, useCallback } from 'react';
import styles from './sidebar.module.scss';
import { SidebarDealershipsProps } from '../../../../interfaces/components';
import { useToast } from '../../../../helpers/hooks';
import { formatRut, validateRut } from '../../../../helpers/app';
import TextFieldShared from '../../../../components/TextField/TextField';
import PhoneInput from '../../../../components/PhoneInput/PhoneInput';
import ButtonShared from '../../../../components/Button/Button';
import SidebarShared from '../../../../components/Sidebar/Sidebar';
import { DEALERSHIPS_TYPES } from '../../../../constants/app';
import { FormControlLabel } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { IOSSwitch } from './sidebar.styles';
import Compressor from 'compressorjs';
import { useDispatch, useSelector } from 'react-redux';
import { ImageCropper } from './components';
import { Area } from 'react-easy-crop/types';
import { postUserImage, cleanPostImage } from '../../../../actions/auth';

const Sidebar = ({
    user,
    state,
    setState,
    dealershipSelected,
    addDealership,
    cleanAddDealership,
    dealershipsImage,
    dealerships,
    token,
    editDealership,
    cleanEditDealership
}: SidebarDealershipsProps) => {
    const toast = useToast();
    const [nameDealership, setNameDealership] = useState<any>({ value: "", error: "" });
    const [directionDealership, setDirectionDealership] = useState<any>({ value: "", error: "" });
    const [name, setName] = useState<any>({ value: "", error: "" });
    const [lastName, setLastName] = useState<any>({ value: "", error: "" });
    const [rut, setRut] = useState<any>({ value: "", error: "" });
    const [email, setEmail] = useState<any>({ value: "", error: "" });
    const [phoneValue, setPhoneValue] = useState<any>({ code: "", number: "", error: "" });
    const [management, setManagement] = useState<string>('managed');
    const [integrations, setIntegrations] = useState<any>([]);
    const [logo, setLogo] = useState<any>({ path: "", file: "" });
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any | null>(null);
    const [newImageProfile, setNewImageProfile] = useState<any>({ path: "", file: "" });
    const [logoFile, setLogoFile] = useState<any>(null);
    const [needsCropping, setNeedsCropping] = useState<boolean>(true);

    const dispatch = useDispatch();
    const { postImage } = useSelector((state: any) => state.auth);

    const cleanup = useCallback(() => {
        dispatch(cleanPostImage() as any);
    }, [dispatch]);

    const integrationsOptions = [
        { id: 'meli', name: 'Mercado Libre' },
        { id: 'chileAutos', name: 'ChileAutos' }
    ];

    const handleChangeIntegrations = (option: any) => {
        const isSelected = integrations.some((item: any) => item.id === option.id);
        let newIntegrations;

        if (isSelected) {
            newIntegrations = integrations.filter((item: any) => item.id !== option.id);
        } else {
            newIntegrations = [...integrations, option];
        }

        setIntegrations(newIntegrations);
    };

    const handleManagementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setManagement(event.target.checked ? 'self-managed' : 'managed');
    };

    const handleChangePhone = (value: any, type: string) => {
        if (type === "code") {
            setPhoneValue({ ...phoneValue, code: value });
        } else {
            setPhoneValue({ ...phoneValue, number: value });
        }
    }

    const formattedIntegrations = integrations.map((item: any) => ({
        code: item.id,
        name: item.name,
        enabled: true
    }));

    const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setLogoFile(file);
        if (file) {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            const imageUrl = URL.createObjectURL(file);
            setPreviewUrl(imageUrl);

            const img = new Image();
            img.onload = () => {
                const imageRatio = img.width / img.height;

                // Verificar si la imagen necesita recorte basado en proporción
                if (Math.abs(imageRatio - 4.5) / 4.5 > 0.1 || img.width < 300) {
                    setNeedsCropping(true);
                } else {
                    setNeedsCropping(false);
                }
            };
            img.src = imageUrl;
        }
    };

    const handleCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const createCroppedImage = async (pixelCrop: any) => {
        if (!previewUrl || !pixelCrop) return null;

        try {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
                image.onload = resolve;
                image.onerror = reject;
                image.src = previewUrl;
            });

            const canvas = document.createElement('canvas');
            canvas.width = pixelCrop.width;
            canvas.height = pixelCrop.height;

            const ctx = canvas.getContext('2d');
            if (!ctx) return null;

            ctx.drawImage(
                image,
                pixelCrop.x,
                pixelCrop.y,
                pixelCrop.width,
                pixelCrop.height,
                0,
                0,
                pixelCrop.width,
                pixelCrop.height
            );

            return new Promise<File | null>((resolve) => {
                canvas.toBlob((blob) => {
                    if (!blob) {
                        resolve(null);
                        return;
                    }

                    const timestamp = new Date().getTime();
                    const file = new File([blob], `cropped-logo-${timestamp}.png`, { 
                        type: 'image/png',
                        lastModified: timestamp
                    });
                    resolve(file);
                }, 'image/png', 0.95);
            });
        } catch (error) {
            console.error('Error creating cropped image:', error);
            return null;
        }
    };
    const submitDealership = useCallback(() => {
        const phone = {
            code: phoneValue?.code,
            number: phoneValue?.number
        };

        const pictureUrl = postImage?.data?.path || dealershipsImage?.path || (state.type === "edit" ? dealershipSelected?.picture : "");

        const bodyRequest: any = {
            "name": nameDealership?.value,
            "address": directionDealership?.value,
            "type": 'used',
            "contact": {
                "name": name?.value,
                "last_name": lastName?.value,
                "email": email?.value,
                "phone": phone,
                "legal_id_type": "rut",
                "legal_id": formatRut(rut?.value),
            },
            "management_type": management,
            "integrations": formattedIntegrations || [],
            "picture": pictureUrl
        };

        if (user?.profile?.code === 'admin-brand' && user?.brand) {
            bodyRequest.brand = user.brand;
        }

        if (state.type === "add") {
            addDealership(bodyRequest);
        } else if (state.type === "edit") {
            editDealership(dealershipSelected?._id, bodyRequest);
        }
    }, [
        phoneValue, dealershipsImage, state.type, dealershipSelected, nameDealership, 
        directionDealership, name, lastName, email, rut, management, formattedIntegrations,
        addDealership, editDealership, postImage, user
    ]);

    const onSubmit = async () => {
        if (!nameDealership?.value) { return setNameDealership({ ...nameDealership, error: "Debes ingresar un nombre" }) } else setNameDealership({ ...nameDealership, error: "" });
        if (!directionDealership?.value) { return setDirectionDealership({ ...directionDealership, error: "Debes seleccionar una dirección" }) } else setDirectionDealership({ ...directionDealership, error: "" });
        if (!name?.value) { return setName({ ...name, error: "Debes ingresar un nombre" }) } else setName({ ...name, error: "" });
        if (!lastName?.value) { return setLastName({ ...lastName, error: "Debes ingresar un apellido" }) } else setLastName({ ...lastName, error: "" });
        if (!email?.value) { return setEmail({ ...email, error: "Debes ingresar un email" }) } else setEmail({ ...email, error: "" });
        if (!rut?.value) { return setRut({ ...rut, error: "Debes ingresar un RUT" }) } else setRut({ ...rut, error: "" });
        if (!phoneValue?.code) { return setPhoneValue({ ...phoneValue, error: "Debes ingresar un pais" }) } else setPhoneValue({ ...phoneValue, error: "" });
        if (!phoneValue?.number) { return setPhoneValue({ ...phoneValue, error: "Debes ingresar un telefono" }) } else setPhoneValue({ ...phoneValue, error: "" });

        if (!validateRut(rut?.value)) {
            return setRut({ ...rut, error: "RUT incorrecto" });
        }

        let finalLogoFile = logoFile;
        if (needsCropping && croppedAreaPixels) {
            const croppedImage = await createCroppedImage(croppedAreaPixels);
            if (croppedImage) {
                finalLogoFile = croppedImage;
            }
        }


        if (finalLogoFile) {
            const formData = new FormData();
            formData.append('file', finalLogoFile);
            dispatch(postUserImage(formData) as any);
        } else {
           
            submitDealership();
        }
    };

    const handleChangeRut = (value: any) => {
        const formatValue = formatRut(value);
        if (!validateRut(formatValue)) {
            setRut({ value: formatValue, error: "Ingresa un RUT válido" });
            return;
        }
        setRut({ value: formatValue, error: "" });
    };

    const dealershipsTypesNormalized: any = useMemo(() => {
        return DEALERSHIPS_TYPES && DEALERSHIPS_TYPES.map((item: any, index: number) => (
            {
                id: item?.id,
                name: item?.label
            }
        ))
    }, [DEALERSHIPS_TYPES]);


    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        if (postImage?.fetching === 'success') {
            submitDealership();
            // Limpiar el estado después de procesar para evitar loops
            dispatch(cleanPostImage() as any);
        }
    }, [postImage?.fetching, submitDealership, dispatch]);

    useEffect(() => {
        return () => cleanup();
    }, [cleanup]);

    useEffect(() => {
        if (postImage?.fetching === "success" && postImage?.data) {
            setLogo({ ...logo, path: postImage.data.path });
        }
    }, [postImage?.fetching, postImage?.data]);

    useEffect(() => {
        if (dealershipSelected && state.type === 'edit') {
            setNameDealership({ value: dealershipSelected?.name, error: "" });
            setDirectionDealership({ value: dealershipSelected?.address, error: "" });
            setName({ value: dealershipSelected?.contact?.name, error: "" });
            setLastName({ value: dealershipSelected?.contact?.last_name, error: "" });
            setRut({ value: formatRut(dealershipSelected?.contact?.legal_id), error: "" });
            setEmail({ value: dealershipSelected?.contact?.email, error: "" });
            setLogo({ path: dealershipSelected?.picture, file: null });
            setPreviewUrl(dealershipSelected?.picture || '');
            setNeedsCropping(false); 
            setPhoneValue({ code: dealershipSelected?.contact?.phone?.code, number: dealershipSelected?.contact?.phone?.number, error: "" });

            if (dealershipSelected?.integrations && dealershipSelected?.integrations.length > 0) {
                const selectedIntegrations = dealershipSelected.integrations.map((integration: any) => ({
                    id: integration.code,
                    name: integration.name
                }));
                setIntegrations(selectedIntegrations);
            } else {
                setIntegrations([]);
            }
            setManagement(dealershipSelected?.management_type || 'managed');
        } else {
            setNameDealership({ value: "", error: "" });
            setDirectionDealership({ value: "", error: "" });
            setName({ value: "", error: "" });
            setLastName({ value: "", error: "" });
            setRut({ value: "", error: "" });
            setEmail({ value: "", error: "" });
            setPhoneValue({ code: "+56", number: "", error: "" });
            setIntegrations([]);
            setManagement('managed');
            setLogo({ path: "", file: null });
            setPreviewUrl('');
            setNeedsCropping(true); 
        }
    }, [dealershipSelected]);

    useEffect(() => {
        if (dealerships?.add?.fetching === "failure") {
            cleanAddDealership();
            if (dealerships?.add?.error === "LEGAL_ID_ALREADY_EXISTS") {
                setRut({ ...rut, error: "El RUT ya existe" })
                return toast("El RUT ya existe");
            }
            if (dealerships?.add?.error === "EMAIL_ALREADY_EXISTS") {
                return toast("El email ya existe");
            }
            if (dealerships?.add?.error === "EMAIL_INVALID_EMAIL") {
                setEmail({ ...email, error: "Ingresa un email valido" });
                return toast("Escribe un email valido");
            }
            toast("Ha ocurrido un error al añadir una nueva concesionaria");
        }
        if (dealerships?.add?.fetching === "success") {
            toast("Concesionaria añadido correctamente");
            setState({ ...state, open: false });
            cleanAddDealership();
        }
    }, [dealerships?.add?.fetching]);

    useEffect(() => {
        if (dealerships?.edit?.fetching === "failure") {
            toast("Ha ocurrido un error al editar la concesionaria");
            cleanEditDealership();
        }
        if (dealerships?.edit?.fetching === "success") {
            toast("Concesionaria editada correctamente");
            setState({ ...state, open: false });
            cleanEditDealership();
            return;
        }
    }, [dealerships?.edit?.fetching]);

    useEffect(() => {
        setState(state);
    }, [state]);

    useEffect(() => {
        if (!state.open) {
            if (state.type === 'add') {
                setPreviewUrl('');
                setLogoFile(null);
                setLogo({ path: "", file: null });
                setNeedsCropping(true);
                setCroppedAreaPixels(null);
                dispatch(cleanPostImage() as any);
            } else if (state.type === 'edit' && !dealershipSelected) {
                setPreviewUrl('');
                setLogoFile(null);
                setLogo({ path: "", file: null });
                setNeedsCropping(true);
                setCroppedAreaPixels(null);
                dispatch(cleanPostImage() as any);
            }
        }
    }, [state.open, state.type, dealershipSelected, dispatch]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <SidebarShared
            variant="close"
            open={state.open}
            title={`${state.type === "add" ? "Nueva" : state.type === "edit" ? "Editar" : "Eliminar"} concesionaria`}
            onBack={() => setState({ ...state, open: false })}
            id="dealershipsSidebarContent"
        >
            <div className={styles.sidebarContent}>
                <div className={styles.inputs}>
                    <TextFieldShared
                        label="Nombre concesionaria"
                        name="nameDealership"
                        value={nameDealership?.value}
                        onChange={(value: any) => setNameDealership({ ...nameDealership, value: value })}
                        error={nameDealership?.error}
                        className={`${styles.full}`}
                    />
                    <TextFieldShared
                        variant="location"
                        label="Dirección concesionaria"
                        name="directionDealership"
                        value={directionDealership?.value}
                        onChange={({ value }: any) => setDirectionDealership({ ...directionDealership, value: value })}
                        error={directionDealership?.error}
                        className={`${styles.full}`}
                    />
                    <SelectShared
                        label="Republicación"
                        name="integrations"
                        variant="filledMultiple"
                        className={`${styles.full}`}
                        options={integrationsOptions}
                        onPressOption={handleChangeIntegrations}
                        multiple
                        value={integrations.map((item: any) => item.id)}
                    />



                    <p className={`${styles.subtitle} ${styles.full}`}>Contacto</p>
                    <div className={styles.inputs}>
                        <TextFieldShared
                            label="Nombre"
                            name="name"
                            value={name?.value}
                            onChange={(value: any) => setName({ ...name, value: value })}
                            error={name?.error}
                            className={`${styles.name} ${styles.half}`}
                        />
                        <TextFieldShared
                            label="Apellido"
                            name="lastName"
                            value={lastName?.value}
                            onChange={(value: any) => setLastName({ ...lastName, value: value })}
                            error={lastName?.error}
                            className={`${styles.lastName} ${styles.half}`}
                        />
                    </div>
                    <TextFieldShared
                        label="RUT"
                        name="rutInput"
                        value={rut?.value}
                        className={`${styles.full}`}
                        onChange={handleChangeRut}
                        error={rut?.error}
                    />
                    <TextFieldShared
                        label="Email"
                        name="email"
                        value={email?.value}
                        onChange={(value: any) => setEmail({ ...email, value: value })}
                        error={email?.error}
                        className={`${styles.full}`}
                    />
                    <PhoneInput
                        label="Telefono"
                        numberValue={phoneValue.number}
                        codeValue={phoneValue.code}
                        onChange={handleChangePhone}
                        error={phoneValue?.error}
                        className={`${styles.inputPhone} ${styles.full}`}
                    />
                    <hr className={styles.divider} />
                    <div className={styles.logoUploadBox}>
                        <p className={styles.subtitle2}>Logo del concesionario</p>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className={styles.fileInput}
                            id="logo-upload"
                        />
                        {!previewUrl ? (
                            <label htmlFor="logo-upload" className={styles.uploadLabel}>
                                <CloudUploadIcon className={styles.uploadIcon} />
                                <span>Haz clic para cargar el logo</span>
                            </label>
                        ) : (
                            <>
                                <div className={styles.cropperWrapper}>
                                    {needsCropping ? (
                                        <ImageCropper
                                            image={previewUrl}
                                            onCropComplete={handleCropComplete}
                                            aspect={4.5}
                                            cropShape="rect"
                                        />
                                    ) : (
                                        <div className={styles.previewContainer}>
                                            <img src={previewUrl} alt="Logo preview" className={styles.fullWidthPreview} />
                                        </div>
                                    )}
                                </div>
                                <label htmlFor="logo-upload" className={styles.changeImageBtn}>
                                    Cambiar imagen
                                </label>
                            </>
                        )}
                    </div>
                    {previewUrl ? (
                        <p className={styles.subtitleLogo}>* Asi se mostrará tu logo</p>
                    ) : (
                        <>
                        </>
                    )}

                </div>
                <ButtonShared
                    onPress={onSubmit}
                    title={
                        state.type === "add"
                            ? "Crear concesionaria"
                            : "Guardar cambios"
                    }
                    className={styles.sendInvitation}
                    primary
                    loading={dealerships?.add?.fetching === "loading" || dealerships?.edit?.fetching === "loading" || postImage?.fetching === "loading"}
                />
            </div>
        </SidebarShared>
    )
};
export default Sidebar;